import type { Currency } from "./money.js";

/**
 * Figures in Spanish text, read as units, whether they are written with
 * digits or in words (DES-89). The checks that keep AI output grounded
 * (legal-change summaries, plain-language explanations, drafted documents)
 * all read figures here, so a figure written as "quince por ciento", "el
 * primero de julio de dos mil veintisiete", "un millón de colones", "treinta
 * días hábiles" or "el próximo año" is as visible to them as "15 %",
 * "2027-07-01", "₡1 000 000" or "30 días hábiles".
 *
 * What is read (one unit each, with its [start, end) range in the text):
 *  - amounts with a currency ("₡4 159 800,00", "US$3,500", "3 mil dólares",
 *    "cuatro millones de colones") and scaled amounts without one ("1,5
 *    millones", "un millón", "quinientos mil");
 *  - percentages ("13 %", "13,5 por ciento", "quince por ciento") and
 *    percentage points ("dos puntos porcentuales");
 *  - dates: "2026-10-15", "15/10/2026", "31.12.2027", "15 de octubre (de
 *    2026)", "1.º de enero", "primero de julio de dos mil veintisiete",
 *    "marzo de 2027". A day and month that do not exist ("31 de junio") are
 *    still read as a date (one no data can ground), never as a bare number;
 *  - terms: a number and a unit ("30 días hábiles", "treinta días", "medio
 *    salario", "tres cuotas", "un mes", "un salario base");
 *  - ordinals with a unit ("el quinto día hábil", "decimoquinto día", "5.º
 *    día hábil", "primer trimestre") or after a legal reference word
 *    ("artículo primero", "transitorio segundo");
 *  - relative dates: "el próximo año", "del mes siguiente", "el año que
 *    viene", "el ejercicio siguiente", "el año anterior", "este año", "al día
 *    siguiente", "mañana", "ayer", "pasado mañana" and "hoy" where it dates
 *    something ("a partir de hoy", "vence hoy"; not "hoy es del 13 %");
 *  - every other number, digits or words ("del quince", "dos mil veintisiete",
 *    "Ley 7092"); digits inside identifiers ("MH-DGT-RES-0099-2026",
 *    "43198-H") are numbers flagged `code`.
 * Articles ("un", "una", "uno") and "medio"/"media" count only with a unit,
 * a currency or a percent sign ("una declaración" and "una vez presentada"
 * are not figures; "un millón", "un mes", "media hora" are). Ordinal words
 * alone ("en primer lugar", "la primera vez") are not figures. Digit runs
 * that cannot be read as a number ("3.5.6") are returned in `unread`, and
 * scale words or percent signs that belong to no figure ("varios millones de
 * colones") in `stray`, so a strict caller can refuse them.
 *
 * Pure and exact: values are canonical decimal strings built with BigInt and
 * string arithmetic, never floats.
 */

export type TermUnit = "dia" | "semana" | "quincena" | "mes" | "trimestre" | "semestre" | "ano" | "periodo" | "hora" | "cuota" | "salario" | "vez";
export type RelativeUnit = "dia" | "semana" | "quincena" | "mes" | "trimestre" | "semestre" | "ano" | "periodo";
/** How many units from now: -1 "anterior", 0 "en curso", 1 "siguiente"; ±2 only for days ("anteayer", "pasado mañana"). */
export type RelativeOffset = -2 | -1 | 0 | 1 | 2;

interface FigureCommon {
  /** [start, end) in the text given to spanishFigures(). */
  start: number;
  end: number;
  /** The text's own characters for the figure. */
  text: string;
  /**
   * The part written in words, folded (lower case, no accents, single
   * spaces): "quinientos cuarenta mil", the scale word of "540 mil", the day
   * and year words of a date. Null when nothing is written in words.
   */
  words: string | null;
  /** True when some part of the figure is written with digits. */
  digits: boolean;
  /** The word right before the figure, folded ("del" in "del quince"); "" when there is none. */
  before: string;
}

export type SpanishFigure = FigureCommon &
  (
    | { kind: "amount"; value: string; currency: Currency | null; scaled: boolean }
    | { kind: "percent"; value: string; points: boolean }
    | { kind: "date"; y: number | null; m: number; d: number | null; month: string | null; dayWords: string | null; yearWords: string | null }
    | { kind: "term"; value: string; unit: TermUnit; qualifier: "habil" | "natural" | null }
    /** "el quinto día hábil" (unit "dia"), "primer trimestre"; unit null after a legal reference word ("artículo primero"). */
    | { kind: "ordinal"; value: string; unit: TermUnit | null; qualifier: "habil" | "natural" | null }
    /**
     * `deictic`: counted from today ("hoy", "mañana", "ayer", "pasado mañana"), not from a date the text names
     * ("el día siguiente", "el siguiente día hábil" after a deadline): "mañana" is never grounded by "el día siguiente".
     */
    | { kind: "relative"; unit: RelativeUnit; offset: RelativeOffset; deictic: boolean }
    | { kind: "number"; value: string; code: boolean }
  );

export interface FigureReading {
  figures: SpanishFigure[];
  /** Digit runs that are not part of any figure (unreadable numbers such as "3.5.6"). */
  unread: { start: number; end: number; text: string }[];
  /**
   * Scale words and percent signs that belong to no figure ("varios millones de colones", "en %"): a figure that
   * could not be read as one. `word` is folded ("millones", "%", "por ciento"); `currency` is the one right after a
   * scale word ("millones de colones"), else null.
   */
  stray: { start: number; end: number; text: string; word: string; currency: Currency | null }[];
}

// ------------------------------------------------------------------ folding

interface Folded {
  /** NFKC, lower case, accents removed, dashes as "-", whitespace as single spaces. */
  t: string;
  /** For each UTF-16 unit of `t`, the [from, to) range of the original text it comes from. */
  from: number[];
  to: number[];
}

const DASH = /[‐-―−﹘﹣－]/u;

function foldText(s: string): Folded {
  let t = "";
  const from: number[] = [];
  const to: number[] = [];
  for (let i = 0; i < s.length; ) {
    const ch = String.fromCodePoint(s.codePointAt(i)!);
    const j = i + ch.length;
    const f = /\s/u.test(ch) ? " " : DASH.test(ch) ? "-" : ch.normalize("NFKC").toLowerCase().normalize("NFD").replace(/\p{M}/gu, "");
    for (const c of f) {
      const out = /\s/u.test(c) ? " " : c;
      if (out === " " && (t === "" || t.endsWith(" "))) continue;
      t += out;
      for (let k = 0; k < out.length; k++) {
        from.push(i);
        to.push(j);
      }
    }
    i = j;
  }
  return { t, from, to };
}

// ------------------------------------------------------------------ numbers

/** An integer part grouped in thousands with "." or ",". */
const THOUSANDS: Readonly<Record<string, RegExp>> = { ".": /^\d{1,3}(?:\.\d{3})+$/, ",": /^\d{1,3}(?:,\d{3})+$/ };

/** "4 159 800,00" -> "4159800"; "13,5" -> "13.5"; "3.000" -> "3000"; "0,125" -> "0.125"; null when not a well-formed number. */
export function canonicalDigits(raw: string): string | null {
  const s = raw.replace(/ /g, "");
  if (!/^\d+(?:[.,]\d+)*$/.test(s)) return null;
  const seps = s.match(/[.,]/g) ?? [];
  let int: string;
  let frac = "";
  if (seps.length === 0) int = s;
  else if (new Set(seps).size === 2) {
    // Both kinds: the last one is the decimal mark, the other groups thousands.
    const last = Math.max(s.lastIndexOf("."), s.lastIndexOf(","));
    const mark = s[last]!;
    const group = mark === "." ? "," : ".";
    const head = s.slice(0, last);
    if (head.includes(mark) || !THOUSANDS[group]!.test(head)) return null;
    int = head.split(group).join("");
    frac = s.slice(last + 1);
  } else if (seps.length > 1) {
    // One kind, several times: thousands groups.
    if (!THOUSANDS[seps[0]!]!.test(s)) return null;
    int = s.split(seps[0]!).join("");
  } else {
    // One separator: thousands when exactly three digits follow (and the integer part is not zero).
    const at = s.search(/[.,]/);
    const head = s.slice(0, at);
    const tail = s.slice(at + 1);
    if (tail.length === 3 && !/^0+$/.test(head)) int = head + tail;
    else {
      int = head;
      frac = tail;
    }
  }
  return canonical(int, frac);
}

function canonical(int: string, frac = ""): string {
  const i = int.replace(/^0+(?=\d)/, "") || "0";
  const f = frac.replace(/0+$/, "");
  return f ? `${i}.${f}` : i;
}

/** Decimal text times 10^places, as text: ("1.5", 6) -> "1500000". */
function shift(value: string, places: number): string {
  const [i = "0", f = ""] = value.split(".");
  const digits = i + f.padEnd(places, "0");
  const cut = i.length + places;
  return canonical(digits.slice(0, cut), digits.slice(cut));
}

const UNITS: Readonly<Record<string, number>> = { cero: 0, un: 1, uno: 1, una: 1, dos: 2, tres: 3, cuatro: 4, cinco: 5, seis: 6, siete: 7, ocho: 8, nueve: 9 };
const TEENS: Readonly<Record<string, number>> = {
  diez: 10, once: 11, doce: 12, trece: 13, catorce: 14, quince: 15, dieciseis: 16, diecisiete: 17, dieciocho: 18, diecinueve: 19,
  veinte: 20, veintiun: 21, veintiuno: 21, veintiuna: 21, veintidos: 22, veintitres: 23, veinticuatro: 24, veinticinco: 25, veintiseis: 26, veintisiete: 27, veintiocho: 28, veintinueve: 29,
};
const TENS: Readonly<Record<string, number>> = { treinta: 30, cuarenta: 40, cincuenta: 50, sesenta: 60, setenta: 70, ochenta: 80, noventa: 90 };
const HUNDREDS: Readonly<Record<string, number>> = {
  cien: 100, ciento: 100, doscientos: 200, doscientas: 200, trescientos: 300, trescientas: 300, cuatrocientos: 400, cuatrocientas: 400, quinientos: 500, quinientas: 500,
  seiscientos: 600, seiscientas: 600, setecientos: 700, setecientas: 700, ochocientos: 800, ochocientas: 800, novecientos: 900, novecientas: 900,
};
/** Scale words: the power of ten they multiply by. */
const SCALES: Readonly<Record<string, number>> = { mil: 3, millon: 6, millones: 6, millardo: 9, millardos: 9, billon: 12, billones: 12 };
const HALF = new Set(["medio", "media"]);
const ARTICLES = new Set(["un", "una", "uno"]);
const has = (o: Readonly<Record<string, number>>, k: string | undefined): k is string => k !== undefined && Object.prototype.hasOwnProperty.call(o, k);
const pow10 = (k: number) => 10n ** BigInt(k);

/** Ordinal words 1-9 (masculine, feminine, apocopated). */
const ORD_UNITS: Readonly<Record<string, number>> = {
  primer: 1, primero: 1, primera: 1, segundo: 2, segunda: 2, tercer: 3, tercero: 3, tercera: 3, cuarto: 4, cuarta: 4, quinto: 5, quinta: 5,
  sexto: 6, sexta: 6, septimo: 7, septima: 7, setimo: 7, setima: 7, octavo: 8, octava: 8, noveno: 9, novena: 9, nono: 9, nona: 9,
};
/** Ordinal tens; with a unit ordinal after them, spaced ("décimo quinto") or joined ("decimoquinto", "decimoctavo"). */
const ORD_TENS: Readonly<Record<string, number>> = {
  decimo: 10, decima: 10, vigesimo: 20, vigesima: 20, trigesimo: 30, trigesima: 30, cuadragesimo: 40, cuadragesima: 40, quincuagesimo: 50, quincuagesima: 50,
  sexagesimo: 60, sexagesima: 60, septuagesimo: 70, septuagesima: 70, octogesimo: 80, octogesima: 80, nonagesimo: 90, nonagesima: 90,
};
const ORD_OTHER: Readonly<Record<string, number>> = { undecimo: 11, undecima: 11, duodecimo: 12, duodecima: 12, centesimo: 100, centesima: 100 };

/** The value of one folded ordinal word ("quinto" 5, "decimoquinto" 15, "decimoctavo" 18, "vigesimoprimer" 21), or null. */
function ordinalWord(w: string): number | null {
  for (const table of [ORD_UNITS, ORD_OTHER, ORD_TENS]) if (Object.prototype.hasOwnProperty.call(table, w)) return table[w]!;
  for (const [tens, v] of Object.entries(ORD_TENS)) {
    // "decimo" + "quinto"; "decim" + "octavo" (the two o's merge).
    for (const stem of [tens, tens.slice(0, -1)]) {
      const rest = w.slice(stem.length);
      if (rest && w.startsWith(stem) && has(ORD_UNITS, rest)) return v + ORD_UNITS[rest]!;
    }
  }
  return null;
}

interface ParsedWords {
  consumed: number;
  value: string;
  /** Power of ten of the last scale word that still applies ("dos millones" 6, "dos mil veintisiete" 0). */
  lastScale: number;
  /** "medio"/"media" alone (0.5): a figure only with a unit or a percent sign. */
  halfOnly: boolean;
}

/** Decimals read after "coma"/"punto": digit words ("cero cinco" -> "05") or one number below 100 ("veinticinco" -> "25"). */
function decimalWords(w: readonly string[], i: number): { digits: string; end: number } | null {
  let digits = "";
  let j = i;
  while (j < w.length && digits.length < 6 && has(UNITS, w[j]) && UNITS[w[j]!]! <= 9) digits += String(UNITS[w[j++]!]);
  if (digits) return { digits, end: j };
  if (has(TEENS, w[i])) return { digits: String(TEENS[w[i]!]), end: i + 1 };
  if (has(TENS, w[i])) {
    const u = w[i + 1] === "y" && has(UNITS, w[i + 2]) && UNITS[w[i + 2]!]! >= 1 ? UNITS[w[i + 2]!]! : 0;
    return { digits: String(TENS[w[i]!]! + u), end: u ? i + 3 : i + 1 };
  }
  return null;
}

/** The longest Spanish number at the start of `w` (folded words), or null. */
function parseWords(w: readonly string[]): ParsedWords | null {
  if (HALF.has(w[0] ?? "")) {
    const k = w[1] === "millon" || w[1] === "millardo" || w[1] === "billon" ? SCALES[w[1]]! : 0;
    if (k) return { consumed: 2, value: (5n * pow10(k - 1)).toString(), lastScale: k, halfOnly: false };
    return { consumed: 1, value: "0.5", lastScale: 0, halfOnly: true };
  }
  let total = 0n;
  let thousands = 0n;
  let group = 0n;
  let stage: "start" | "hundred" | "cien" | "tens" | "done" = "start";
  let milUsed = false;
  let lastMillion = 99;
  let lastScale = 0;
  let j = 0;
  let best: { end: number; int: bigint; lastScale: number } | null = null;
  for (;;) {
    const x = w[j];
    if (x === undefined) break;
    const open = stage === "start" || stage === "hundred";
    if (has(HUNDREDS, x) && stage === "start" && group === 0n) {
      group += BigInt(HUNDREDS[x]!);
      stage = x === "cien" ? "cien" : "hundred";
      lastScale = 0;
      j++;
    } else if (has(TENS, x) && open) {
      group += BigInt(TENS[x]!);
      stage = "tens";
      lastScale = 0;
      j++;
    } else if (x === "y" && stage === "tens" && has(UNITS, w[j + 1]) && UNITS[w[j + 1]!]! >= 1) {
      group += BigInt(UNITS[w[j + 1]!]!);
      stage = "done";
      j += 2;
    } else if (has(TEENS, x) && open) {
      group += BigInt(TEENS[x]!);
      stage = "done";
      lastScale = 0;
      j++;
    } else if (has(UNITS, x) && open && !(x === "cero" && (j > 0 || w[j + 1] === "mil"))) {
      group += BigInt(UNITS[x]!);
      stage = "done";
      lastScale = 0;
      j++;
      if (x === "cero") {
        best = { end: j, int: 0n, lastScale: 0 };
        break;
      }
    } else if (x === "mil" && !milUsed) {
      thousands = (group === 0n ? 1n : group) * 1000n;
      group = 0n;
      stage = "start";
      milUsed = true;
      lastScale = 3;
      j++;
    } else if (has(SCALES, x) && SCALES[x]! >= 6 && SCALES[x]! < lastMillion && (group > 0n || thousands > 0n)) {
      total += (thousands + group) * pow10(SCALES[x]!);
      thousands = 0n;
      group = 0n;
      stage = "start";
      milUsed = false;
      lastMillion = SCALES[x]!;
      lastScale = SCALES[x]!;
      j++;
    } else break;
    best = { end: j, int: total + thousands + group, lastScale };
  }
  if (!best) return null;
  let { end, int } = best;
  let frac = "";
  if ((w[end] === "coma" || w[end] === "punto") && best.lastScale === 0) {
    const d = decimalWords(w, end + 1);
    if (d) {
      frac = d.digits;
      end = d.end;
    }
  }
  if (!frac && w[end] === "y" && HALF.has(w[end + 1] ?? "")) {
    if (best.lastScale === 0) frac = "5";
    else int += 5n * pow10(best.lastScale - 1);
    end += 2;
  }
  return { consumed: end, value: canonical(int.toString(), frac), lastScale: best.lastScale, halfOnly: false };
}

/** The value of a whole Spanish number phrase ("dos mil veintisiete" -> "2027", "trece coma cinco" -> "13.5"), or null. */
export function parseSpanishNumber(phrase: string): string | null {
  const w = foldText(phrase).t.trim().split(" ").filter(Boolean);
  const p = parseWords(w);
  return p && p.consumed === w.length && !p.halfOnly ? p.value : null;
}

// ------------------------------------------------------------------ patterns (on folded text)

const MONTHS: Readonly<Record<string, number>> = {
  enero: 1, febrero: 2, marzo: 3, abril: 4, mayo: 5, junio: 6, julio: 7, agosto: 8, septiembre: 9, setiembre: 9, octubre: 10, noviembre: 11, diciembre: 12,
};
const MONTH = Object.keys(MONTHS).join("|");
const CURRENCY_OF: Readonly<Record<string, Currency>> = {
  "₡": "CRC", "¢": "CRC", crc: "CRC", colon: "CRC", colones: "CRC",
  $: "USD", usd: "USD", dolar: "USD", dolares: "USD",
  "€": "EUR", eur: "EUR", euro: "EUR", euros: "EUR",
};
const TERM_UNIT: Readonly<Record<string, TermUnit>> = {
  dia: "dia", dias: "dia", semana: "semana", semanas: "semana", quincena: "quincena", quincenas: "quincena", mes: "mes", meses: "mes",
  trimestre: "trimestre", trimestres: "trimestre", semestre: "semestre", semestres: "semestre", ano: "ano", anos: "ano",
  periodo: "periodo", periodos: "periodo", hora: "hora", horas: "hora", cuota: "cuota", cuotas: "cuota", salario: "salario", salarios: "salario", vez: "vez", veces: "vez",
};

const ISO_DATE = /(?<![\d.,/-])(\d{4})-(\d{1,2})-(\d{1,2})(?!\d)/g;
const DMY_DATE = /(?<![\d.,/-])(\d{1,2})([/-])(\d{1,2})\2(\d{4}|\d{2})(?![\d/-])/g;
// A period right after the year ends the sentence ("Rige el 31.12.2027."); "31.12.2027.5" is not a date.
const DMY_DOT_DATE = /(?<![\d.,/-])(\d{1,2})\.(\d{1,2})\.(\d{4})(?!\d|\.\d)/g;
// A space-grouped figure ("4 159 800,00") is one number; otherwise digits with dots and commas.
const DIGITS = /(?<!\d)(?:\d{1,3}(?: \d{3})+(?:[.,]\d+)?(?!\d)|\d+(?:[.,]\d+)*)/g;
// Identifiers: letters and digits joined without spaces ("mh-dgt-res-0099-2026", "43198-h", "in202601041613").
const TOKEN = /(?<![a-z0-9])[a-z0-9]+(?:[-_:/][a-z0-9]+)*(?![a-z0-9])/g;
const ORDINAL_TOKEN = /^\d+(?:o|a|os|as|er|era|ero|ro|ra|do|da|to|ta|vo|va|no|na|mo|ma|avo|ava)$/;
const DAY_TAIL = new RegExp(String.raw`^(?: ?\.? ?(?:o|°|ro|ero|er))? de (${MONTH})(?![a-z])`);
const YEAR_JOIN = /^ (?:(?:de|del) )?(?:ano )?/;
const MONTH_YEAR = new RegExp(String.raw`(?<![a-z])(${MONTH}) (?:(?:de|del) )?(?:ano )?(?=[0-9a-z])`, "g");
const CUR_BEFORE = /(us ?\$|\$|₡|¢|€|(?<![a-z])(?:crc|usd|eur)) ?$/;
const CUR_AFTER = /^ (?:de )?(colones|colon|dolares|dolar|euros|euro|crc|usd|eur)(?![a-z])/;
const SCALE_AFTER = /^ ?(mil|millones|millon|millardos|millardo|billones|billon)(?![a-z])|^k(?![a-z0-9])/;
const PCT_AFTER = /^ ?(?:%|por ?ciento(?![a-z])|porciento(?![a-z]))/;
const PP_AFTER = /^ (?:puntos?|pts?\.?) porcentual(?:es)?(?![a-z])/;
const HABIL = String.raw`(?: (habiles|habil|naturales|natural|calendario)(?![a-z]))?`;
const UNIT_AFTER = new RegExp(String.raw`^ (${Object.keys(TERM_UNIT).join("|")})(?![a-z])${HABIL}`);
// After an ordinal: "el quinto día hábil", "primer trimestre". Not "la primera vez", "a primera hora", "segundo salario".
const ORD_UNIT_AFTER = new RegExp(String.raw`^ (${Object.keys(TERM_UNIT).filter((u) => !["vez", "veces", "hora", "horas", "salario", "salarios"].includes(u)).join("|")})(?![a-z])${HABIL}`);
const qualifierOf = (q: string | undefined): "habil" | "natural" | null => (q ? (q.startsWith("habil") ? "habil" : "natural") : null);
// "1.º", "1o", "1.ª", "1er", "2do", "5to", "15avo": a digit ordinal (directly attached; "1 o 2" and "1 a 10" are not).
const ORD_SUFFIX = /^(?:\.?(?:o|a|°)|er|ero|era|ro|ra|do|da|to|ta|vo|va|no|na|mo|ma|avo|ava)(?![a-z0-9])/;
/** Words after which an ordinal is a legal reference ("artículo primero", "transitorio segundo", "inciso tercero"). */
const LEGAL_LEAD: ReadonlySet<string> = new Set([
  "art", "arts", "articulo", "articulos", "inciso", "incisos", "numeral", "numerales", "transitorio", "transitorios", "capitulo", "capitulos",
  "titulo", "titulos", "parrafo", "parrafos", "apartado", "apartados", "seccion", "secciones", "libro", "resultando", "considerando",
]);
const REL_UNITS = "ano|mes|semana|quincena|dia|periodo|ejercicio|trimestre|semestre";
const REL_NEXT = "proximo|proxima|siguiente|entrante|venidero|venidera";
const REL_PREV = "pasado|pasada|anterior|precedente";
const RELATIVE = new RegExp(
  String.raw`(?<![a-z])(?:(${REL_NEXT}|${REL_PREV}|este|esta|presente) (${REL_UNITS})(?: fiscal)?|(${REL_UNITS})(?: fiscal)? (${REL_NEXT}|${REL_PREV}|que viene|que entra|en curso|corriente|actual))(?![a-z])`,
  "g",
);
// Days named in words. "hoy" dates something only after a date word or before a deadline verb ("a partir de hoy",
// "hasta hoy", "vence hoy", "hoy mismo", "hoy vence"): "hoy es del 13 %" and "hoy en día" mean "currently".
// "mañana" is "tomorrow", not "la mañana" / "por la mañana" / "el día de mañana" (morning, someday).
const HOY_LEAD = "de|desde|hasta|para|vence|vencen|vencio|venza|venzan|rige|rigen|regira|regiran|a mas tardar";
const HOY_TAIL = "mismo|misma|vence|vencen|vencio|rige|rigen|entra|entran|termina|terminan|inicia|inician|comienza|comienzan|empieza|empiezan|se cumple|se cumplen|es el ultimo";
const DAY_WORD = new RegExp(
  String.raw`(?<![a-z])(?:(pasado manana)|(anteayer|antier|antes de ayer)|(?<=(?<![a-z])(?:${HOY_LEAD}) )(hoy)|(hoy)(?= (?:${HOY_TAIL})(?![a-z]))|(?<!(?<![a-z])(?:la|las|una|esta|cada|media|toda|misma|dia de) )(manana)|(ayer))(?![a-z])`,
  "g",
);
// Scale words and percent signs, to report the ones no figure uses.
const STRAY = /(?<![a-z])(millon|millones|millardos?|billon|billones)(?![a-z])|%|(?<![a-z])(?:por ?ciento|porciento)(?![a-z])/g;

const offsetOf = (w: string): -1 | 0 | 1 =>
  new RegExp(`^(?:${REL_NEXT}|que viene|que entra)$`).test(w) ? 1 : new RegExp(`^(?:${REL_PREV})$`).test(w) ? -1 : 0;
const relUnitOf = (w: string): RelativeUnit => (w === "ejercicio" ? "periodo" : (w as RelativeUnit));

/**
 * A month and a day that can be written (1-31), even when the month has fewer days: "31 de junio" is read as a
 * date no data will ground, never as a loose "31" a caller might let through.
 */
function plausibleDate(m: number, d: number | null): boolean {
  return m >= 1 && m <= 12 && (d === null || (d >= 1 && d <= 31));
}

// ------------------------------------------------------------------ reading

interface Atom {
  s: number;
  e: number;
  /** Canonical value; null for unreadable digits. */
  value: string | null;
  /** The raw digits ("15", "2026") for digit atoms; null for words. */
  raw: string | null;
  words: string | null;
  article: boolean;
  ordinal: boolean;
  halfOnly: boolean;
  lastScale: number;
  code: boolean;
  used: boolean;
}

type Draft = Omit<FigureCommon, "start" | "end" | "text" | "before"> & { s: number; e: number } & Record<string, unknown>;

const isInt = (v: string | null): v is string => v !== null && /^\d+$/.test(v);

function dayOf(a: Atom): number | null {
  // "primero de julio", "1.º de julio"; other ordinal words ("quinto de julio") are not how dates are written.
  if (a.ordinal && a.raw === null && a.value !== "1") return null;
  if (a.article && a.words !== "uno") return null;
  const ok = a.raw !== null ? /^\d{1,2}$/.test(a.raw) : !a.halfOnly;
  const n = ok && isInt(a.value) ? Number(a.value) : 0;
  return n >= 1 && n <= 31 ? n : null;
}

function yearOf(a: Atom | undefined): number | null {
  if (!a || a.used || a.code || a.article || a.halfOnly || a.ordinal) return null;
  if (a.raw !== null && !/^\d{4}$/.test(a.raw)) return null;
  const n = isInt(a.value) ? Number(a.value) : 0;
  return n >= 1900 && n <= 2199 ? n : null;
}

/** Every figure of `text`, in order, plus unreadable digit runs. See the module comment. */
export function spanishFigures(text: string): FigureReading {
  const { t, from, to } = foldText(text);
  const drafts: Draft[] = [];
  let masked = t;
  const mask = (s: number, e: number) => {
    masked = masked.slice(0, s) + "#".repeat(e - s) + masked.slice(e);
  };

  // 1. Dates written only with digits.
  const digitDate = (re: RegExp, read: (m: RegExpMatchArray) => [number, number, number]) => {
    for (const m of t.matchAll(re)) {
      const s = m.index ?? 0;
      const e = s + m[0].length;
      if (masked.slice(s, e).includes("#")) continue;
      const [y, mo, d] = read(m);
      if (!plausibleDate(mo, d)) continue;
      drafts.push({ s, e, kind: "date", y, m: mo, d, month: null, dayWords: null, yearWords: null, words: null, digits: true });
      mask(s, e);
    }
  };
  digitDate(ISO_DATE, (m) => [Number(m[1]), Number(m[2]), Number(m[3])]);
  digitDate(DMY_DATE, (m) => [m[4]!.length === 2 ? 2000 + Number(m[4]) : Number(m[4]), Number(m[3]), Number(m[1])]);
  digitDate(DMY_DOT_DATE, (m) => [Number(m[3]), Number(m[2]), Number(m[1])]);

  // 2. Numbers: digit runs and runs of number words.
  const codes: [number, number][] = [];
  for (const m of masked.matchAll(TOKEN)) {
    const tok = m[0];
    if (/[a-z]/.test(tok) && /\d/.test(tok) && !ORDINAL_TOKEN.test(tok) && !/^\d+k$/.test(tok)) codes.push([m.index ?? 0, (m.index ?? 0) + tok.length]);
  }
  const atoms: Atom[] = [];
  for (const m of masked.matchAll(DIGITS)) {
    const s = m.index ?? 0;
    let e = s + m[0].length;
    const code = codes.some(([a, b]) => s >= a && e <= b);
    // "5.º", "1er", "2do": a digit ordinal, suffix included.
    const suffix = !code && /^\d+$/.test(m[0]) ? ORD_SUFFIX.exec(masked.slice(e)) : null;
    if (suffix) e += suffix[0].length;
    atoms.push({ s, e, value: canonicalDigits(m[0]), raw: m[0], words: null, article: false, ordinal: suffix !== null, halfOnly: false, lastScale: 0, code, used: false });
  }
  const tokens = [...masked.matchAll(/[a-z]+/g)].map((m) => ({ w: m[0], s: m.index ?? 0, e: (m.index ?? 0) + m[0].length }));
  const spaced = (i: number) => i + 1 < tokens.length && masked.slice(tokens[i]!.e, tokens[i + 1]!.s) === " ";
  for (let k = 0; k < tokens.length; ) {
    const first = tokens[k]!.w;
    const ord = ordinalWord(first);
    if (ord !== null) {
      // "décimo quinto", "vigésimo primer": a tens ordinal and a unit ordinal, spaced.
      const unitWord = ord % 10 === 0 && ord < 100 && spaced(k) ? tokens[k + 1]!.w : "";
      const joined = has(ORD_UNITS, unitWord);
      const last = joined ? tokens[k + 1]! : tokens[k]!;
      const value = joined ? ord + ORD_UNITS[unitWord]! : ord;
      atoms.push({ s: tokens[k]!.s, e: last.e, value: String(value), raw: null, words: joined ? `${first} ${unitWord}` : first, article: false, ordinal: true, halfOnly: false, lastScale: 0, code: false, used: false });
      k += joined ? 2 : 1;
      continue;
    }
    const porCiento = (first === "ciento" || first === "cien") && tokens[k - 1]?.w === "por" && masked.slice(tokens[k - 1]!.e, tokens[k]!.s) === " ";
    if (porCiento || !(has(UNITS, first) || has(TEENS, first) || has(TENS, first) || has(HUNDREDS, first) || first === "mil" || HALF.has(first))) {
      k++;
      continue;
    }
    // The words that follow, separated by single spaces only.
    const run = [tokens[k]!];
    while (run.length < 16 && k + run.length < tokens.length && masked.slice(run.at(-1)!.e, tokens[k + run.length]!.s) === " ") run.push(tokens[k + run.length]!);
    const p = parseWords(run.map((x) => x.w));
    if (!p || p.consumed === 0) {
      k++;
      continue;
    }
    const used = run.slice(0, p.consumed);
    const words = used.map((x) => x.w).join(" ");
    atoms.push({
      s: used[0]!.s,
      e: used.at(-1)!.e,
      value: p.value,
      raw: null,
      words,
      article: p.consumed === 1 && ARTICLES.has(first),
      ordinal: false,
      halfOnly: p.halfOnly,
      lastScale: p.lastScale,
      code: false,
      used: false,
    });
    k += p.consumed;
  }
  atoms.sort((a, b) => a.s - b.s);
  const atomAt = new Map(atoms.map((a) => [a.s, a] as const));

  // 3. Day and month (and year): "15 de marzo de 2026", "primero de julio de dos mil veintisiete".
  for (const a of atoms) {
    if (a.used || a.code || a.value === null) continue;
    const day = dayOf(a);
    if (day === null) continue;
    const tail = DAY_TAIL.exec(t.slice(a.e));
    if (!tail) continue;
    const month = tail[1]!;
    let e = a.e + tail[0].length;
    let y: number | null = null;
    let yearWords: string | null = null;
    let yearDigits = false;
    const join = YEAR_JOIN.exec(t.slice(e));
    const ya = join ? atomAt.get(e + join[0].length) : undefined;
    const year = yearOf(ya);
    if (year !== null && ya) {
      y = year;
      yearWords = ya.words;
      yearDigits = ya.raw !== null;
      ya.used = true;
      e = ya.e;
    }
    a.used = true;
    const dayWords = a.raw === null ? a.words : null;
    drafts.push({ s: a.s, e, kind: "date", y, m: MONTHS[month]!, d: day, month, dayWords, yearWords, words: [dayWords, yearWords].filter(Boolean).join(" ") || null, digits: a.raw !== null || yearDigits });
  }

  // 4. Month and year: "marzo de 2027", "julio del año dos mil veintisiete".
  for (const m of t.matchAll(MONTH_YEAR)) {
    const s = m.index ?? 0;
    const ya = atomAt.get(s + m[0].length);
    const year = yearOf(ya);
    if (year === null || !ya || masked.slice(s, s + m[1]!.length).includes("#") || drafts.some((d) => s < d.e && s + m[1]!.length > d.s)) continue;
    ya.used = true;
    drafts.push({ s, e: ya.e, kind: "date", y: year, m: MONTHS[m[1]!]!, d: null, month: m[1]!, dayWords: null, yearWords: ya.words, words: ya.words, digits: ya.raw !== null });
  }

  // 5. Relative dates: "el próximo año", "del mes siguiente", "el año que viene", "mañana".
  for (const m of t.matchAll(RELATIVE)) {
    const s = m.index ?? 0;
    const e = s + m[0].length;
    if (drafts.some((d) => s < d.e && e > d.s)) continue;
    drafts.push({ s, e, kind: "relative", unit: relUnitOf(m[2] ?? m[3]!), offset: offsetOf(m[1] ?? m[4]!), deictic: false, words: null, digits: false });
  }
  for (const m of t.matchAll(DAY_WORD)) {
    const s = m.index ?? 0;
    const e = s + m[0].length;
    if (drafts.some((d) => s < d.e && e > d.s)) continue;
    const offset: RelativeOffset = m[1] ? 2 : m[2] ? -2 : m[3] || m[4] ? 0 : m[5] ? 1 : -1;
    drafts.push({ s, e, kind: "relative", unit: "dia", offset, deictic: true, words: null, digits: false });
  }

  // 6. Every other number, with what is written around it.
  const leadOf = (s: number) => /([a-z]+)[^a-z0-9]{0,3}$/.exec(t.slice(Math.max(0, s - 30), s))?.[1] ?? "";
  for (const a of atoms) {
    // "ciento" of "13 por ciento" belongs to the percentage read just before it.
    if (a.used || a.value === null || drafts.some((d) => a.s >= d.s && a.e <= d.e)) continue;
    a.used = true;
    const digits = a.raw !== null;
    if (a.code) {
      drafts.push({ s: a.s, e: a.e, kind: "number", value: a.value, code: true, words: null, digits: true });
      continue;
    }
    if (a.ordinal) {
      // "el quinto día hábil", "primer trimestre", "artículo primero"; alone ("en primer lugar") an ordinal word is
      // not a figure, and a digit ordinal ("10a") stays the number it was.
      const u = ORD_UNIT_AFTER.exec(t.slice(a.e));
      if (u) drafts.push({ s: a.s, e: a.e + u[0].length, kind: "ordinal", value: a.value, unit: TERM_UNIT[u[1]!]!, qualifier: qualifierOf(u[2]), words: a.words, digits });
      else if (LEGAL_LEAD.has(leadOf(a.s))) drafts.push({ s: a.s, e: a.e, kind: "ordinal", value: a.value, unit: null, qualifier: null, words: a.words, digits });
      else if (digits) drafts.push({ s: a.s, e: a.e, kind: "number", value: a.value, code: false, words: null, digits: true });
      continue;
    }
    let s = a.s;
    let e = a.e;
    let value = a.value;
    let words = a.words;
    let scaled = a.lastScale > 0;
    if (digits) {
      const sc = SCALE_AFTER.exec(t.slice(e));
      if (sc) {
        value = shift(value, sc[1] ? SCALES[sc[1]]! : 3);
        words = sc[1] ?? null;
        scaled = true;
        e += sc[0].length;
      }
    }
    const after = t.slice(e);
    const cb = CUR_BEFORE.exec(t.slice(Math.max(0, s - 6), s));
    // "uno" after a legal reference word is the number one ("artículo uno", "Ley uno"), not an article.
    const figureLike = (!a.article || (a.words === "uno" && (LEGAL_LEAD.has(leadOf(a.s)) || /^(?:ley|leyes|decreto|decretos|numero|n)$/.test(leadOf(a.s))))) && !a.halfOnly;
    let m: RegExpExecArray | null;
    if (cb && !a.halfOnly) {
      const currency = CURRENCY_OF[cb[1]!.replace(/^us ?/, "")]!;
      s -= cb[0].length;
      const ca = CUR_AFTER.exec(after);
      if (ca && CURRENCY_OF[ca[1]!] === currency) e += ca[0].length;
      drafts.push({ s, e, kind: "amount", value, currency, scaled, words, digits });
    } else if ((m = PCT_AFTER.exec(after))) {
      drafts.push({ s, e: e + m[0].length, kind: "percent", value, points: false, words, digits });
    } else if ((m = PP_AFTER.exec(after))) {
      drafts.push({ s, e: e + m[0].length, kind: "percent", value, points: true, words, digits });
    } else if (!a.halfOnly && (m = CUR_AFTER.exec(after))) {
      drafts.push({ s, e: e + m[0].length, kind: "amount", value, currency: CURRENCY_OF[m[1]!]!, scaled, words, digits });
    } else if (!scaled && (m = UNIT_AFTER.exec(after)) && !(a.article && TERM_UNIT[m[1]!] === "vez")) {
      // "un mes", "un salario base" are terms of one; "una vez presentada" is not a figure.
      drafts.push({ s, e: e + m[0].length, kind: "term", value, unit: TERM_UNIT[m[1]!]!, qualifier: qualifierOf(m[2]), words, digits });
    } else if (scaled) {
      drafts.push({ s, e, kind: "amount", value, currency: null, scaled: true, words, digits });
    } else if (figureLike) {
      drafts.push({ s, e, kind: "number", value, code: false, words, digits });
    }
  }

  drafts.sort((x, y) => x.s - y.s);
  const figures = drafts.map((d) => {
    const { s, e, ...rest } = d;
    const start = from[s]!;
    const end = to[e - 1]!;
    return { ...rest, start, end, text: text.slice(start, end), before: leadOf(s) } as SpanishFigure;
  });
  const inFigure = (s: number, e: number) => drafts.some((d) => s >= d.s && e <= d.e);
  const unread: FigureReading["unread"] = [];
  for (const m of t.matchAll(/\d+(?:[.,]\d+)*/g)) {
    const s = m.index ?? 0;
    const e = s + m[0].length;
    if (inFigure(s, e)) continue;
    unread.push({ start: from[s]!, end: to[e - 1]!, text: text.slice(from[s]!, to[e - 1]!) });
  }
  const stray: FigureReading["stray"] = [];
  for (const m of t.matchAll(STRAY)) {
    const s = m.index ?? 0;
    let e = s + m[0].length;
    if (inFigure(s, e)) continue;
    const ca = m[1] ? CUR_AFTER.exec(t.slice(e)) : null;
    if (ca) e += ca[0].length;
    stray.push({ start: from[s]!, end: to[e - 1]!, text: text.slice(from[s]!, to[e - 1]!), word: m[1] ?? m[0], currency: ca ? CURRENCY_OF[ca[1]!]! : null });
  }
  return { figures, unread, stray };
}

// ------------------------------------------------------------------ helpers for callers

/** Words before a number that make it a figure rather than a count ("del quince", "art. 5", "hasta dos"). */
export const FIGURE_LEAD_WORDS: ReadonlySet<string> = new Set([
  "del", "el", "al", "hasta", "desde", "antes", "despues", "vence", "vencimiento", "tarifa", "tasa", "alicuota", "porcentaje", "monto", "suma", "total",
  "art", "arts", "articulo", "articulos", "inciso", "numeral", "ley", "decreto", "transitorio", "formulario", "numero", "n", "nro", "num", "dia", "plazo",
]);

/** A small whole number standing alone ("las dos obligaciones"), not a figure: at most `max`, no lead word. */
export function isBareCount(f: SpanishFigure, max: number): boolean {
  return f.kind === "number" && !f.code && /^\d+$/.test(f.value) && Number(f.value) <= max && !FIGURE_LEAD_WORDS.has(f.before);
}

/** True when the words of a figure use a scale of a million or more ("un millón", "dos millones"): an amount "in millions". */
export function inMillions(f: SpanishFigure): boolean {
  return f.words !== null && /(?<![a-z])(?:millon|millones|millardos?|billon|billones)(?![a-z])/.test(f.words);
}

/** Month + day/year key of a date ("2027-7-1", "-7-1", "2027-7-"). */
export function dateKey(f: { y: number | null; m: number; d: number | null }): string {
  return `${f.y ?? ""}-${f.m}-${f.d ?? ""}`;
}

/** Singular Spanish label of a relative date ("año siguiente", "mes anterior", "año en curso"; "hoy", "mañana", "pasado mañana"). */
export function relativeLabel(f: { unit: RelativeUnit; offset: RelativeOffset; deictic?: boolean }): string {
  if (f.deictic) return { [-2]: "anteayer", [-1]: "ayer", 0: "hoy", 1: "mañana", 2: "pasado mañana" }[f.offset];
  const unit = f.unit === "ano" ? "año" : f.unit === "dia" ? "día" : f.unit === "periodo" ? "período" : f.unit;
  if (f.offset === 2 || f.offset === -2) return `${f.offset > 0 ? "dos" : "menos dos"} ${unit}`;
  return f.offset === 1 ? `${unit} siguiente` : f.offset === -1 ? `${unit} anterior` : `${unit} en curso`;
}

/** Comparable key of a relative date: "ano:1", "dia:1" ("el día siguiente"), "dia:1:hoy" ("mañana", counted from today). */
export function relativeKey(f: { unit: RelativeUnit; offset: RelativeOffset; deictic?: boolean }): string {
  return `${f.unit}:${f.offset}${f.deictic ? ":hoy" : ""}`;
}
